import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { AppShell } from "../components/Layout";
import { api } from "../lib/api";
import { useHasPermission } from "../context/AuthContext";
import {
  ShieldCheck, Plus, Search, Calendar, MapPin, FileText, Upload,
  BellOff, Bell, Pencil, Trash2, ExternalLink, X, Check, AlertTriangle,
} from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "../components/ui/dialog";

/**
 * Phase L1 — Licenses vertical page.
 * Overall view: all licenses across sites, sorted soonest-expiry first.
 * ?site=X — filters to a single site.
 * Only visible to users with `view_licenses`. Mutations require `manage_licenses`.
 */
const STATUS = {
  "Active":         { pill: "bg-emerald-100 text-emerald-800 border-emerald-200", ring: "ring-emerald-200" },
  "Expiring soon":  { pill: "bg-amber-100 text-amber-900 border-amber-200",     ring: "ring-amber-200" },
  "Expired":        { pill: "bg-red-100 text-red-800 border-red-200",             ring: "ring-red-200" },
};
const DEFAULT_REMINDER_DAYS = [60, 30, 7];
const PRESET_REMINDER_DAYS = [90, 60, 45, 30, 15, 7, 3, 1];

export default function LicensesPage() {
  const canView = useHasPermission("view_licenses");
  const canManage = useHasPermission("manage_licenses");
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [siteFilter, setSiteFilter] = useState("");
  const [showDismissed, setShowDismissed] = useState(false);
  const [dialog, setDialog] = useState(null);   // { mode:'create'|'edit', row }
  const [meta, setMeta] = useState({ sites: [], categories: [] });

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (siteFilter) params.site = siteFilter;
      if (showDismissed) params.include_dismissed = true;
      const [lic, sum, m] = await Promise.all([
        api.get("/licenses", { params }),
        api.get("/licenses-summary").catch(() => ({ data: null })),
        api.get("/sites").catch(() => ({ data: { sites: [] } })),
      ]);
      setRows(lic.data || []);
      setSummary(sum.data);
      setMeta(m.data || { sites: [] });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load licenses");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [siteFilter, showDismissed]);

  const filtered = useMemo(() => {
    if (!q) return rows;
    const needle = q.toLowerCase();
    return rows.filter((r) =>
      (r.name || "").toLowerCase().includes(needle) ||
      (r.license_number || "").toLowerCase().includes(needle) ||
      (r.issuing_authority || "").toLowerCase().includes(needle) ||
      (r.site || "").toLowerCase().includes(needle));
  }, [rows, q]);

  const sites = useMemo(() => Array.from(new Set([...(meta.sites || []), ...rows.map((r) => r.site)])).filter(Boolean), [meta, rows]);

  if (!canView) {
    return (
      <AppShell>
        <div className="max-w-md mx-auto text-center py-24"><div className="text-sm text-slate-500">Not found</div></div>
      </AppShell>
    );
  }

  const onSaved = () => { setDialog(null); load(); };

  const dismiss = async (r) => {
    try {
      await api.post(`/licenses/${r.license_id}/dismiss`);
      toast.success("Reminders stopped for this license");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to dismiss"); }
  };
  const undismiss = async (r) => {
    try {
      await api.post(`/licenses/${r.license_id}/undismiss`);
      toast.success("Reminders re-enabled");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to undismiss"); }
  };
  const del = async (r) => {
    if (!window.confirm(`Delete "${r.name}" (${r.license_number})? This cannot be undone.`)) return;
    try {
      await api.delete(`/licenses/${r.license_id}`);
      toast.success("License deleted");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to delete"); }
  };

  return (
    <AppShell>
      <div className="max-w-6xl mx-auto space-y-6" data-testid="licenses-page">
        {/* Header */}
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-2xl bg-olive text-white flex items-center justify-center">
              <ShieldCheck size={20} />
            </div>
            <div>
              <h1 className="font-display font-semibold text-2xl tracking-tight text-slate-900">Licenses</h1>
              <div className="text-xs text-slate-500 mt-0.5">
                Per-site registry with expiry tracking · reminders daily at 09:00 IST
              </div>
            </div>
          </div>
          {canManage && (
            <button
              data-testid="add-license-btn"
              onClick={() => setDialog({ mode: "create" })}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-slate-900 text-white text-sm font-medium hover:bg-black transition"
            >
              <Plus size={16} /> Add license
            </button>
          )}
        </div>

        {/* Summary */}
        {summary && (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="licenses-summary">
            <SummaryCard label="Total" value={summary.total} tone="slate" />
            <SummaryCard label="Active" value={summary.active} tone="emerald" />
            <SummaryCard label="Expiring soon" value={summary.expiring} tone="amber" />
            <SummaryCard label="Expired" value={summary.expired} tone="red" />
          </div>
        )}

        {/* Toolbar */}
        <div className="flex flex-wrap items-center gap-3">
          <div className="relative flex-1 min-w-[220px]">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              data-testid="licenses-search"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search by name, number, site…"
              className="w-full pl-9 pr-3 py-2 rounded-lg border border-slate-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-olive/30"
            />
          </div>
          <select
            data-testid="licenses-site-filter"
            value={siteFilter}
            onChange={(e) => setSiteFilter(e.target.value)}
            className="px-3 py-2 rounded-lg border border-slate-200 bg-white text-sm"
          >
            <option value="">All sites</option>
            {sites.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
          <label className="flex items-center gap-2 text-xs text-slate-600 cursor-pointer select-none">
            <input
              type="checkbox"
              data-testid="licenses-show-dismissed"
              checked={showDismissed}
              onChange={(e) => setShowDismissed(e.target.checked)}
              className="rounded"
            />
            Show archived
          </label>
        </div>

        {/* Table */}
        <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden" data-testid="licenses-table">
          {loading ? (
            <div className="py-16 text-center text-sm text-slate-400">Loading…</div>
          ) : filtered.length === 0 ? (
            <div className="py-16 text-center text-sm text-slate-400">
              {rows.length === 0 ? "No licenses yet. Add your first one to start tracking expiry." : "No licenses match your search."}
            </div>
          ) : (
            <div className="divide-y divide-slate-100">
              {filtered.map((r) => (
                <LicenseRow
                  key={r.license_id}
                  r={r}
                  canManage={canManage}
                  onEdit={() => setDialog({ mode: "edit", row: r })}
                  onDismiss={() => dismiss(r)}
                  onUndismiss={() => undismiss(r)}
                  onDelete={() => del(r)}
                />
              ))}
            </div>
          )}
        </div>
      </div>

      {dialog && (
        <LicenseDialog
          mode={dialog.mode}
          row={dialog.row}
          meta={meta}
          onClose={() => setDialog(null)}
          onSaved={onSaved}
        />
      )}
    </AppShell>
  );
}

function SummaryCard({ label, value, tone }) {
  const tones = {
    slate:   "border-slate-200 bg-white",
    emerald: "border-emerald-200 bg-emerald-50/60",
    amber:   "border-amber-200 bg-amber-50/60",
    red:     "border-red-200 bg-red-50/60",
  };
  return (
    <div
      data-testid={`summary-${label.toLowerCase().replace(/\s+/g, "-")}`}
      className={`rounded-2xl border p-4 ${tones[tone] || tones.slate}`}
    >
      <div className="text-[10px] font-medium uppercase tracking-wider text-slate-500">{label}</div>
      <div className="mt-1 font-display font-semibold text-2xl text-slate-900 tabular-nums">{value}</div>
    </div>
  );
}

function LicenseRow({ r, canManage, onEdit, onDismiss, onUndismiss, onDelete }) {
  const s = STATUS[r.status] || STATUS.Active;
  const daysLabel = r.days_until_expiry == null
    ? "—"
    : r.days_until_expiry < 0
      ? `${Math.abs(r.days_until_expiry)} day${Math.abs(r.days_until_expiry) === 1 ? "" : "s"} ago`
      : r.days_until_expiry === 0
        ? "today"
        : `in ${r.days_until_expiry} day${r.days_until_expiry === 1 ? "" : "s"}`;
  return (
    <div
      data-testid={`license-row-${r.license_id}`}
      className={`px-4 py-3 flex items-center gap-3 hover:bg-slate-50 transition ${r.renewal_dismissed ? "opacity-60" : ""}`}
    >
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-semibold text-sm text-slate-900 truncate">{r.name}</span>
          <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded border ${s.pill}`}>
            {r.status}
          </span>
          {r.renewal_dismissed && (
            <span className="text-[10px] font-medium uppercase tracking-wider px-2 py-0.5 rounded bg-slate-100 text-slate-500 border border-slate-200">
              Archived
            </span>
          )}
        </div>
        <div className="text-xs text-slate-500 mt-0.5 truncate flex items-center gap-2 flex-wrap">
          <span className="inline-flex items-center gap-1"><MapPin size={11}/>{r.site}</span>
          <span>·</span>
          <span>#{r.license_number || "—"}</span>
          {r.issuing_authority && <><span>·</span><span>{r.issuing_authority}</span></>}
        </div>
      </div>
      <div className="text-right shrink-0">
        <div className="text-sm font-semibold text-slate-900 tabular-nums">{r.expiry_date}</div>
        <div className="text-[11px] text-slate-500">{daysLabel}</div>
      </div>
      {canManage && (
        <div className="flex items-center gap-1">
          {r.doc_url && (
            <a
              href={r.doc_url}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => e.stopPropagation()}
              data-testid={`license-doc-${r.license_id}`}
              title="View certificate"
              className="p-2 rounded-lg text-slate-500 hover:text-slate-900 hover:bg-slate-100"
            >
              <FileText size={14}/>
            </a>
          )}
          {r.status === "Expired" && !r.renewal_dismissed && (
            <button
              data-testid={`license-dismiss-${r.license_id}`}
              onClick={onDismiss}
              title="Stop reminders (not renewing)"
              className="p-2 rounded-lg text-amber-700 hover:text-amber-900 hover:bg-amber-50"
            >
              <BellOff size={14}/>
            </button>
          )}
          {r.renewal_dismissed && (
            <button
              data-testid={`license-undismiss-${r.license_id}`}
              onClick={onUndismiss}
              title="Re-enable reminders"
              className="p-2 rounded-lg text-slate-500 hover:text-slate-900 hover:bg-slate-100"
            >
              <Bell size={14}/>
            </button>
          )}
          <button
            data-testid={`license-edit-${r.license_id}`}
            onClick={onEdit}
            title="Edit / renew"
            className="p-2 rounded-lg text-slate-500 hover:text-slate-900 hover:bg-slate-100"
          >
            <Pencil size={14}/>
          </button>
          <button
            data-testid={`license-delete-${r.license_id}`}
            onClick={onDelete}
            title="Delete"
            className="p-2 rounded-lg text-slate-500 hover:text-red-700 hover:bg-red-50"
          >
            <Trash2 size={14}/>
          </button>
        </div>
      )}
    </div>
  );
}

function LicenseDialog({ mode, row, meta, onClose, onSaved }) {
  const [form, setForm] = useState(() => ({
    name: row?.name || "",
    site: row?.site || (meta.sites?.[0] || ""),
    license_number: row?.license_number || "",
    issuing_authority: row?.issuing_authority || "",
    issue_date: row?.issue_date || "",
    expiry_date: row?.expiry_date || "",
    reminder_days: row?.reminder_days || DEFAULT_REMINDER_DAYS,
    notes: row?.notes || "",
  }));
  const [file, setFile] = useState(null);
  const [saving, setSaving] = useState(false);

  const toggleDay = (n) => {
    setForm((f) => {
      const has = f.reminder_days.includes(n);
      const next = has ? f.reminder_days.filter((x) => x !== n) : [...f.reminder_days, n];
      next.sort((a, b) => b - a);
      return { ...f, reminder_days: next };
    });
  };

  const save = async () => {
    if (!form.name.trim() || !form.expiry_date || !form.site) {
      toast.error("Name, site and expiry date are required");
      return;
    }
    setSaving(true);
    try {
      let lid;
      if (mode === "create") {
        const r = await api.post("/licenses", form);
        lid = r.data.license_id;
      } else {
        await api.patch(`/licenses/${row.license_id}`, form);
        lid = row.license_id;
      }
      // Optional doc upload
      if (file) {
        const fd = new FormData();
        fd.append("file", file);
        await api.post(`/licenses/${lid}/doc`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      }
      toast.success(mode === "create" ? "License added" : "License updated");
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={true} onOpenChange={(o) => !o && onClose()}>
      <DialogContent
        className="max-w-lg border border-slate-200 rounded-2xl p-0 overflow-hidden"
        data-testid="license-dialog"
      >
        <DialogHeader className="px-5 pt-5 pb-3 border-b border-slate-200">
          <DialogTitle className="font-display font-semibold text-lg tracking-tight text-slate-900">
            {mode === "create" ? "Add license" : "Edit license"}
          </DialogTitle>
          <DialogDescription className="text-xs text-slate-500 mt-1">
            All fields except issuing authority + notes are required.
          </DialogDescription>
        </DialogHeader>

        <div className="px-5 py-4 space-y-3 max-h-[65vh] overflow-y-auto">
          <FormRow label="Name / type"><input data-testid="license-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="input" placeholder="e.g. FSSAI, Trade License"/></FormRow>
          <FormRow label="Site">
            <select data-testid="license-site" value={form.site} onChange={(e) => setForm({ ...form, site: e.target.value })} className="input">
              <option value="">Select site</option>
              {(meta.sites || []).map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </FormRow>
          <FormRow label="License number"><input data-testid="license-number" value={form.license_number} onChange={(e) => setForm({ ...form, license_number: e.target.value })} className="input"/></FormRow>
          <FormRow label="Issuing authority (optional)"><input data-testid="license-authority" value={form.issuing_authority} onChange={(e) => setForm({ ...form, issuing_authority: e.target.value })} className="input"/></FormRow>
          <div className="grid grid-cols-2 gap-3">
            <FormRow label="Issue date"><input type="date" data-testid="license-issue" value={form.issue_date} onChange={(e) => setForm({ ...form, issue_date: e.target.value })} className="input"/></FormRow>
            <FormRow label="Expiry date *"><input type="date" data-testid="license-expiry" value={form.expiry_date} onChange={(e) => setForm({ ...form, expiry_date: e.target.value })} className="input"/></FormRow>
          </div>
          <FormRow label="Reminder lead times (days before expiry)">
            <div className="flex flex-wrap gap-2" data-testid="license-reminders">
              {PRESET_REMINDER_DAYS.map((n) => {
                const on = form.reminder_days.includes(n);
                return (
                  <button
                    key={n}
                    type="button"
                    data-testid={`reminder-${n}`}
                    onClick={() => toggleDay(n)}
                    className={`px-3 py-1.5 rounded-full text-xs font-medium border transition ${
                      on ? "bg-olive text-white border-olive" : "bg-white text-slate-700 border-slate-300 hover:bg-slate-50"
                    }`}
                  >
                    {n}d
                  </button>
                );
              })}
            </div>
          </FormRow>
          <FormRow label="Certificate (optional — PDF or image)">
            <label className="flex items-center gap-2 px-3 py-2 rounded-lg border border-dashed border-slate-300 text-xs text-slate-600 cursor-pointer hover:bg-slate-50">
              <Upload size={14}/>
              <input type="file" data-testid="license-file" hidden onChange={(e) => setFile(e.target.files?.[0] || null)}/>
              {file ? file.name : (row?.doc_filename || "Click to upload")}
            </label>
          </FormRow>
          <FormRow label="Notes"><textarea data-testid="license-notes" rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="input"/></FormRow>
        </div>

        <div className="border-t border-slate-200 px-5 py-3 flex items-center justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 text-xs font-medium text-slate-700 rounded-lg border border-slate-300 hover:bg-slate-50">Cancel</button>
          <button
            data-testid="license-save"
            onClick={save}
            disabled={saving}
            className="px-4 py-2 text-xs font-semibold text-white rounded-lg bg-olive hover:bg-olive-700 disabled:opacity-40 transition"
          >
            {saving ? "Saving…" : (mode === "create" ? "Add license" : "Save")}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

const FormRow = ({ label, children }) => (
  <div>
    <div className="text-[10px] font-medium uppercase tracking-wider text-slate-500 mb-1">{label}</div>
    <style>{`.input{width:100%;padding:0.5rem 0.75rem;border-radius:0.5rem;border:1px solid #cbd5e1;background:white;font-size:0.875rem;outline:none;}
      .input:focus{box-shadow:0 0 0 3px rgb(107 127 63 / 0.3);border-color:#6B7F3F;}
      textarea.input{resize:none;}`}</style>
    {children}
  </div>
);
